import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PhoneOff, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { QUICK_LEAD_STATUSES, STATUS_META } from "@/lib/constants";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/** One-tap lead status (Not Interested / Not Receiving / Not Reachable) without opening the lead. */
export default function QuickStatusMenu({ lead }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: async (status) =>
      api.post(`/leads/${lead.id}/activities`, { type: "status_change", status_from: lead.status, status_to: status }),
    onSuccess: (_, status) => {
      toast.success(`${lead.name} → ${STATUS_META[status].label}`);
      ["leads", "lead", "dashboard", "notifications"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update status"),
  });
  const stop = (e) => e.stopPropagation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={stop}>
        <button
          data-testid={`lead-quick-status-${lead.id}`}
          disabled={mut.isPending}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-100"
        >
          <PhoneOff className="h-3.5 w-3.5" /> {mut.isPending ? "Saving…" : "Quick status"} <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={stop}>
        {QUICK_LEAD_STATUSES.map((s) => (
          <DropdownMenuItem
            key={s}
            data-testid={`lead-quick-status-${lead.id}-${s}`}
            disabled={lead.status === s}
            onSelect={() => mut.mutate(s)}
            className="gap-2"
          >
            <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s].color }} />
            {STATUS_META[s].label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
